import axios, { AxiosInstance } from 'axios'

import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import {
  AIChatConversationResponse,
  AIChatConversationsListResponse,
  AIChatConversationWithMessagesResponse,
  AIChatCreateConversationPayload,
  AIChatDeleteConversationResponse,
  AIChatSendMessagePayload,
  AIChatSendMessageResponse,
} from 'src/interfaces/aiChat'
import { CreditsResponse } from 'src/interfaces/credits'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'

const getAiChatApiHeaders = () => ({
  'Access-Control-Allow-Origin': ANALYTICS_API_URL,
  'Content-Type': 'application/json',
  Vary: 'Origin',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

export class AiChatApi {
  private static _instance: AiChatApi

  private axiosInstance: AxiosInstance

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getAiChatApiHeaders(),
    })

    // Headers baked in at construction time go stale: the singleton is rebuilt
    // on logout (when there is no session id) and is not rebuilt on the next
    // login, so every AI-chat call kept sending an empty x-session-id and got
    // 401 until a full page reload. Re-read the session id per request.
    this.axiosInstance.interceptors.request.use((config) => {
      config.headers.set('x-session-id', getSessionIdFromLocalStorage() || '')

      return config
    })
  }

  private static get instance(): AiChatApi {
    if (!AiChatApi._instance) {
      AiChatApi._instance = new AiChatApi()
    }

    return AiChatApi._instance
  }

  public static refresh(): void {
    AiChatApi._instance = new AiChatApi()
  }

  // GET /v1_private/ai-chat/conversations
  public static async getConversations(): Promise<AIChatConversationsListResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.get<AIChatConversationsListResponse>(
        '/v1_private/ai-chat/conversations'
      )
    return data
  }

  // POST /v1_private/ai-chat/conversations
  public static async createConversation(
    payload?: AIChatCreateConversationPayload
  ): Promise<AIChatConversationResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.post<AIChatConversationResponse>(
        '/v1_private/ai-chat/conversations',
        payload
      )
    return data
  }

  // GET /v1_private/ai-chat/conversations/:conversationId
  public static async getConversation(
    conversationId: string
  ): Promise<AIChatConversationWithMessagesResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.get<AIChatConversationWithMessagesResponse>(
        `/v1_private/ai-chat/conversations/${conversationId}`
      )
    return data
  }

  // POST /v1_private/ai-chat/conversations/:conversationId/messages
  public static async sendMessage(
    conversationId: string,
    payload: AIChatSendMessagePayload
  ): Promise<AIChatSendMessageResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.post<AIChatSendMessageResponse>(
        `/v1_private/ai-chat/conversations/${conversationId}/messages`,
        payload
      )
    return data
  }

  // DELETE /v1_private/ai-chat/conversations/:conversationId
  public static async deleteConversation(
    conversationId: string
  ): Promise<AIChatDeleteConversationResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.delete<AIChatDeleteConversationResponse>(
        `/v1_private/ai-chat/conversations/${conversationId}`
      )
    return data
  }

  // GET /v1_private/credits
  public static async getCredits(): Promise<CreditsResponse> {
    const { data } =
      await AiChatApi.instance.axiosInstance.get<CreditsResponse>(
        '/v1_private/credits'
      )
    return data
  }
}
